/**
 * Fill scan-direction swatch. Pure geometry + SVG-string builder (no DOM) so it
 * is unit-testable. Renders parallel scan lines at the given angle with
 * direction arrows: bi-directional = back-and-forth serpentine (arrows alternate,
 * solid turn connectors), uni-directional = every line sweeps the same way with
 * a faint dashed non-engraving flyback — matching how laser software depicts the
 * two fill modes.
 */

/** Clip segment (ax,ay)-(bx,by) to an axis-aligned rect; null if fully outside. */
export function clipSegToRect(
  ax: number, ay: number, bx: number, by: number,
  x0: number, y0: number, x1: number, y1: number,
): [[number, number], [number, number]] | null {
  const dx = bx - ax;
  const dy = by - ay;
  let t0 = 0;
  let t1 = 1;
  const p = [-dx, dx, -dy, dy];
  const q = [ax - x0, x1 - ax, ay - y0, y1 - ay];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) return null; // parallel and outside
    } else {
      const r = q[i] / p[i];
      if (p[i] < 0) {
        if (r > t1) return null;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return null;
        if (r < t1) t1 = r;
      }
    }
  }
  return [[ax + t0 * dx, ay + t0 * dy], [ax + t1 * dx, ay + t1 * dy]];
}

export function scanAnglePreviewSVG(angleDeg: number, bidir: boolean): string {
  // Inset the scan lines from the card edge so nothing touches the rounded frame.
  const X0 = 18, Y0 = 18, X1 = 82, Y1 = 82, C = 50;
  const th = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(th), dy = Math.sin(th); // scan-line direction
  const px = -Math.sin(th), py = Math.cos(th); // spacing direction (perp)
  const spacing = 18;
  const L = 90;
  const f = (n: number): string => n.toFixed(2);
  const ks = [-1.5, -0.5, 0.5, 1.5];
  const segs = ks.map((k) => {
    const ox = C + px * spacing * k;
    const oy = C + py * spacing * k;
    return clipSegToRect(ox - dx * L, oy - dy * L, ox + dx * L, oy + dy * L, X0, Y0, X1, Y1);
  });
  const parts: string[] = [];
  // Card: soft rounded background + hairline frame (styled via CSS classes).
  parts.push(`<rect class="sap-card" x="4" y="4" width="92" height="92" rx="14"/>`);
  const arrow = (tip: [number, number], ux: number, uy: number): string => {
    const s = 6.5;
    const bx = tip[0] - ux * s, by = tip[1] - uy * s;
    const nx = -uy, ny = ux;
    const p1x = bx + nx * s * 0.42, p1y = by + ny * s * 0.42;
    const p2x = bx - nx * s * 0.42, p2y = by - ny * s * 0.42;
    return `<polygon class="sap-arrow" points="${f(tip[0])},${f(tip[1])} ${f(p1x)},${f(p1y)} ${f(p2x)},${f(p2y)}"/>`;
  };
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    if (!seg) continue;
    const [A, B] = seg; // A = −d end, B = +d end
    parts.push(`<line class="sap-line" x1="${f(A[0])}" y1="${f(A[1])}" x2="${f(B[0])}" y2="${f(B[1])}"/>`);
    if (bidir) {
      // Alternate travel direction; connect the finishing end of this line to the
      // starting end of the next on the same side (the serpentine turn).
      const evenTip = i % 2 === 0;
      parts.push(evenTip ? arrow(B, dx, dy) : arrow(A, -dx, -dy));
      const next = segs[i + 1];
      if (next) {
        const from = evenTip ? B : A;
        const to = evenTip ? next[1] : next[0];
        parts.push(`<line class="sap-turn" x1="${f(from[0])}" y1="${f(from[1])}" x2="${f(to[0])}" y2="${f(to[1])}"/>`);
      }
    } else {
      // All lines sweep +d; the non-engraving flyback returns to the next line.
      parts.push(arrow(B, dx, dy));
      const next = segs[i + 1];
      if (next) {
        parts.push(`<line class="sap-return" x1="${f(B[0])}" y1="${f(B[1])}" x2="${f(next[0][0])}" y2="${f(next[0][1])}"/>`);
      }
    }
  }
  return `<svg viewBox="0 0 100 100" class="sap-svg" aria-hidden="true">${parts.join("")}</svg>`;
}
